import { useCallback } from 'react'

import { PageLayout } from '@components/layouts/PageLayout'
import type { CallingSessionQueryType } from '@frontend/graphql/queries/callingSession'
import { CallingSessionSupportersDataTable } from '../components/CallingSessionSupportersDataTable'
import { buildSessionSupportersData } from './ViewAllSupportersSheet'
import { InitiateCallingSessionDialog } from '../features/start-automated-calls/InitiateCallingSessionDialog'
import { SkipSupporterDialog } from '../features/skip-supporter/SkipSupporterDialog'
import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'
import { ImpactPanel } from '../components/calling-session-dashboard/ImpactPanel'
import { CallingJourneyPanel } from '../components/calling-session-dashboard/CallingJourneyPanel'
import { router } from '@inertiajs/react'
import { callingSessionSupporterUrl } from '../../supporters-management/utils/routes'
import { startDisabledReason, type StartCallingPermissions } from '../utils/startDisabledReason'

const callingSessionsShowRegistry: DialogRegistry = {
  initiateCallingSession: InitiateCallingSessionDialog,
  skipSupporter: SkipSupporterDialog,
}

type SharedInertiaProps = {
  permissions: StartCallingPermissions
}

type CallingSessionsShowType = CallingSessionQueryType & SharedInertiaProps

const CallingSessionsShow = ({ callingSession, permissions }: React.PropsWithChildren<CallingSessionsShowType>) => {
  const {
    name,
    sessionSupporters: sessionSupportersData,
    nextAvailableSessionSupporterToCall,
    nextSessionSupporterToCall,
    sessionProgress,
  } = callingSession
  const nextSupporterToCall = nextSessionSupporterToCall?.supporter
  const nextAvailableSupporter = nextAvailableSessionSupporterToCall?.supporter
  const sessionSupporters = buildSessionSupportersData(sessionSupportersData, nextAvailableSupporter?.id)

  const handleSuccess = useCallback(() => {
    const supporterId = nextAvailableSupporter?.id ?? sessionSupporters[0]?.supporterId
    if (supporterId) {
      router.visit(callingSessionSupporterUrl(callingSession.id, supporterId))
    }
  }, [nextAvailableSupporter?.id, sessionSupporters, callingSession.id])

  return (
    <DialogProvider registry={callingSessionsShowRegistry}>
      <PageLayout.Root>
        <PageLayout.Header icon="phone-call-01" heading={name!} />
        <AppAlerts alertsPath="viewAllSupporters" className="tw:px-6" />
        <PageLayout.Body>
          <div className="tw:grid tw:grid-cols-1 tw:xl:grid-cols-2 tw:gap-6 tw:mb-5">
            <ImpactPanel
              callingSessionId={callingSession.id}
              callInProgressAt={callingSession.callInProgressAt ?? null}
              isPaused={callingSession.paused}
              totalCallsAttemptedCount={callingSession.totalCallsAttemptedCount}
              successfulContactsCount={callingSession.successfulContactsCount}
              unsuccessfulContactsCount={callingSession.unsuccessfulContactsCount}
              completedSupportersCount={callingSession.completedSupportersCount}
              availableSupportersThisCycleCount={callingSession.availableSupportersThisCycleCount}
              sessionProgress={sessionProgress ?? { percentage: 0, completedCount: 0, totalSupportersCount: 0 }}
              currentCycle={callingSession.currentCycle ?? null}
              showViewAllButton
              onCallingSessionStarted={handleSuccess}
              lastAttemptedSupporter={callingSession.lastAttemptedSessionSupporter?.supporter}
              startDisabledReason={startDisabledReason(permissions)}
            />
            <CallingJourneyPanel completedCycles={callingSession.completedCycles} />
          </div>

          <div className="tw:pb-6">
            <CallingSessionSupportersDataTable
              sessionSupporters={sessionSupporters}
              callingSessionId={callingSession.id}
              nextSupporterToCallId={nextSupporterToCall?.id}
              showOutcome
            />
          </div>
        </PageLayout.Body>
      </PageLayout.Root>
    </DialogProvider>
  )
}

export default CallingSessionsShow
